import { type FC } from 'react';
import classNames from 'classnames';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';
import * as tracking from 'dibs-tracking';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';

import { DesignSeriesCollectionItem } from '../DesignSeriesCollectionItem/DesignSeriesCollectionItem';
import { type DesignSeriesCreatorCollection_iconicDesignSeriesPage$data } from './__generated__/DesignSeriesCreatorCollection_iconicDesignSeriesPage.graphql';

import styles from './DesignSeriesCreatorCollection.scss';

type Props = {
    iconicDesignSeriesPage: DesignSeriesCreatorCollection_iconicDesignSeriesPage$data;
};

const DesignSeriesCreatorCollectionComponent: FC<Props> = props => {
    const { iconicDesignSeriesPage } = props;
    const { displayName, designs, cmsTopModule } = iconicDesignSeriesPage.primaryCreator || {};
    const { description } = cmsTopModule || {};

    if (!designs || !designs.length) {
        return null;
    }
    const hasLinkableDesigns = designs.some(design => design?.linkData?.isLinkable);
    if (!hasLinkableDesigns) {
        return null;
    }

    return (
        <div
            className={classNames(styles.collectionWrapper, {
                [styles.nullDescription]: !description,
            })}
        >
            <HeadingLevel>
                {Heading => (
                    <Heading className={styles.heading} dataTn="design-series-creator-heading">
                        <FormattedMessage
                            defaultMessage="More {displayName} Designs"
                            id="designSeries.DesignSeriesCreatorCollection.heading"
                            values={{ displayName }}
                        />
                    </Heading>
                )}
            </HeadingLevel>
            <div className={styles.items} data-tn="design-series-creator-items">
                {designs.map((design, idx) => {
                    if (!design) {
                        return null;
                    }
                    return (
                        <DesignSeriesCollectionItem
                            design={design}
                            size="small"
                            key={design.displayName || idx}
                            onClick={() => {
                                tracking.trackEvent({
                                    category: 'promo interaction',
                                    action: 'more from iconic design creator clicked',
                                    label: 'design series page',
                                });
                            }}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export const DesignSeriesCreatorCollection = createFragmentContainer(
    DesignSeriesCreatorCollectionComponent,
    {
        iconicDesignSeriesPage: graphql`
            fragment DesignSeriesCreatorCollection_iconicDesignSeriesPage on IconicDesignSeriesPage {
                primaryCreator(maxDesignsCount: $primaryCreatorDesignsMaxCount) {
                    displayName
                    cmsTopModule {
                        description
                    }
                    designs {
                        displayName
                        linkData {
                            isLinkable
                        }
                        ...DesignSeriesCollectionItem_design
                    }
                }
            }
        `,
    }
);
